import React, { useState, useEffect, useRef } from 'react';
import {
  Printer,
  Tags,
  BarChart3,
  Info,
  Settings,
  Package2,
  PenTool,
    Plus,
    ScanBarcode
} from 'lucide-react';
import { PalletSummaryDto, CreateOrderSSCCRequest } from '../../../../types/picking';
import { pickingService } from '../../../../services/pickingService';
import { stcBinService } from '../../../../services/stcBinService';
import { dataInspectionService } from '../../../../services/dataInspectionService';
import { mandatoryCountingService } from '../../../../services/mandatoryCountingService';
import { BackButton } from '../../../common/BackButton';
import { MediaCaptureButton } from '../../../common/media/MediaCaptureButton';
import { GS1Scanner, GS1ScannerRef, GS1ScanData } from './GS1Scanner';
import { PalletManager } from './PalletManager';
import { ActiveLine } from './ActiveLine';
import { PickingLinesList } from './PickingLinesList';
import { ImagePanel } from './ImagePanel';
import { StatusBar } from './StatusBar';
import {
  LocationScanModal,
  LocationOverrideModal,
  ImageModal,
  SplitModal,
  QuantityDistributionModal,
  PalletInfoModal,
  DeliveryInfoModal,
  QuantityConfirmModal,
  QuantityDeviationModal,
  SignatureModal,
  SettingsModal,
  PrintModal,
  PickingPrintModal,
  MandatoryCountingModal,
  ZeroPointCountingModal,
  PackageScanModal,
  SerialNumberModal,
  SerialPickModal,
  BatchPickModal,
  BatchNumberModal,
  AddLineModal,
  PackZoneSelectionModal
} from './modals';
import { GS1Data } from './types';
import { usePalletManagement } from '../../../../hooks/usePalletManagement';
import { isEmptyPallet, isDatabasePallet } from '../../../../types/picking';
import { useSimpleBarcodeScanner } from '../../../../hooks/useSimpleBarcodeScanner';
import { useBarcodeScannerEnabled } from '../../../../hooks/useBarcodeScannerEnabled';
import { MobileBarcodeCaptureInput } from '../../../common/scanning';
import { parseGS1Barcode as parseGS1BarcodeFull } from '../../../../utils/gs1Parser';
import { printingService } from '../../../../services/printingService';
import { SSCCLabelType } from '../../../../types/printing';
import { useAuth } from '../../../../contexts/AuthContext';
import { usePickingData, ExtendedPickingLine } from './hooks/usePickingData';
import { usePickingCompletion } from './hooks/usePickingCompletion';
import { usePickingSplit } from './hooks/usePickingSplit';

interface ModernPickingDetailsProps {
  pListId: number;
  onBack: () => void;
}

export const ModernPickingDetails: React.FC<ModernPickingDetailsProps> = ({
  pListId,
  onBack
}) => {
  const { user } = useAuth();

  // Refs
  const scannerRef = useRef<GS1ScannerRef>(null);

  // ─── Pallet management hook ───
  const {
    pallets,
    activePalletSSCC,
    activePallet,
    emptyPallets,
    databasePallets,
    setActivePalletSSCC,
    loadPallets,
    createEmptyPallet,
    deleteEmptyPallet
  } = usePalletManagement(pListId);

  // Convert UnifiedPallet[] to PalletSummaryDto[] for components
  const palletSummaries: PalletSummaryDto[] = pallets.map(p => {
    if (isEmptyPallet(p)) {
      return {
        sscc: p.sscc,
        ssccLevel: p.ssccLevel,
        totalItems: 0,
        totalQuantity: 0,
        totalWeight: 0,
        isActive: p.sscc === activePalletSSCC,
        created: p.created,
        createdBy: p.createdBy,
        items: []
      };
    } else {
      return {
        ...p,
        isActive: p.sscc === activePalletSSCC
      };
    }
  });

  // Convert activePallet to PalletSummaryDto format
  const activePalletSummary: PalletSummaryDto | undefined = activePallet
    ? palletSummaries.find(p => p.sscc === activePallet.sscc)
    : undefined;

  // ─── Data hook (loading, settings, helpers) ───
  const pickingData = usePickingData(pListId, palletSummaries);
  const {
    pickingDetails, pickingLines, setPickingLines, loading, error,
    userProfile, pListOrders, setPListOrders,
    activeLineIndex, setActiveLineIndex, currentLineIndex, setCurrentLineIndex,
    lineStartTimes, setLineStartTimes,
    activeLine, currentLine, completedLines,
    enableRapidScanning, setEnableRapidScanning,
    autoShowImage, setAutoShowImage,
    enableDragAndDrop, setEnableDragAndDrop,
    zeroPointCountingEnabled, fewPointCountingThreshold,
    loadPickingDetails, loadPListOrders, loadUserProfile,
    reorganizeLines, findLineByItem, convertToPickingLine, convertToPickingLineWithCheck,
  } = pickingData;

  // ─── Completion hook (complete picking, send to packing, delivery info) ───
  const completion = usePickingCompletion({
    pListId, pickingDetails, pickingLines, pListOrders, setPListOrders,
    emptyPallets, loadPickingDetails, onBack,
  });
  const {
    showDeliveryInfoModal, setShowDeliveryInfoModal,
    showPackZoneSelection, setShowPackZoneSelection,
    showQuantityDeviation, quantityDeviations,
    showSignatureModal, setShowSignatureModal,
    handleCompletePicking, handleSendToPacking, handlePackZoneConfirm,
    handleDeliveryInfoConfirm, handleQuantityDeviationConfirm, handleQuantityDeviationCancel,
    handleSignatureConfirm,
  } = completion;

  // ─── Split/Distribution hook ───
  const split = usePickingSplit({
    pListId, pickingLines, setPickingLines, setActiveLineIndex,
    palletSummaries, lineStartTimes, loadPallets, reorganizeLines,
  });
  const {
    showSplitModal, splitLineNo, splitQuantities, setSplitQuantities,
    showQuantityDistribution, distributionLineNo, distributionQuantities, setDistributionQuantities,
    handleSplitLine, confirmSplit, closeSplitModal,
    confirmQuantityDistribution, closeDistributionModal,
  } = split;

  // ─── Remaining local UI state (tightly coupled to picking flow) ───
  const [showPalletManager, setShowPalletManager] = useState(false);
  const [printingActivePalletSSCC, setPrintingActivePalletSSCC] = useState(false);

  // Image state
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string>('');
  const [showImagePanel, setShowImagePanel] = useState(false);

  // Settings/Print modals
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [showPickingPrintModal, setShowPickingPrintModal] = useState(false);

  // Location scanning
  const [showLocationScan, setShowLocationScan] = useState(false);
  const [locationScan, setLocationScan] = useState('');
  const [showLocationOverride, setShowLocationOverride] = useState(false);
  const [overrideLocation, setOverrideLocation] = useState('');
  const [tempGS1Quantity, setTempGS1Quantity] = useState<number | null>(null);

  // Store pending GS1 scan data for use after location/quantity validation
  const [pendingGS1Data, setPendingGS1Data] = useState<GS1ScanData | null>(null);

  // Editing states
  const [editingLocation, setEditingLocation] = useState<number | null>(null);
  const [newLocation, setNewLocation] = useState('');
  const [editingQuantity, setEditingQuantity] = useState<number | null>(null);
  const [newQuantity, setNewQuantity] = useState('');

  // Pallet info
  const [showPalletInfo, setShowPalletInfo] = useState<string | null>(null);

  // Quantity confirmation modal
  const [showQuantityConfirm, setShowQuantityConfirm] = useState(false);
  const [quantityToConfirm, setQuantityToConfirm] = useState<number>(0);
  const [lineToConfirm, setLineToConfirm] = useState<ExtendedPickingLine | null>(null);

  // Package scan modal (for batch picking)
  const [showPackageScan, setShowPackageScan] = useState(false);

  // Mandatory counting modal
  const [showMandatoryCounting, setShowMandatoryCounting] = useState(false);
  const [mandatoryCountingData, setMandatoryCountingData] = useState<{
    itemNo: string;
    itemDescription: string;
    binCode: string;
    lastCountedDate?: string | null;
    daysSinceLastCounted?: number | null;
    currentQuantity: number;
  } | null>(null);
  const [isSubmittingCounting, setIsSubmittingCounting] = useState(false);

  // Zero-point counting modal
  const [showZeroPointCounting, setShowZeroPointCounting] = useState(false);
  const [zeroPointCountingData, setZeroPointCountingData] = useState<{
    itemNo: string;
    itemDescription: string;
    binCode: string;
    pickedQuantity: number;
    expectedRemaining: number;
    warehouse: string;
    pListLineNo: number;
  } | null>(null);
  const [isSubmittingZeroPoint, setIsSubmittingZeroPoint] = useState(false);

  // Serial number registration modal (ItemType=2 - free-text entry for documentation)
  const [showSerialNumberModal, setShowSerialNumberModal] = useState(false);
  const [serialNumberLine, setSerialNumberLine] = useState<ExtendedPickingLine | null>(null);

  // Serial pick modal (ItemType=6 - select from available serials in inventory)
  const [showSerialPickModal, setShowSerialPickModal] = useState(false);
  const [serialPickLine, setSerialPickLine] = useState<ExtendedPickingLine | null>(null);

  // Batch pick modal (ItemType=4/5 - select batch from available batches in inventory)
  const [showBatchPickModal, setShowBatchPickModal] = useState(false);
  const [batchPickLine, setBatchPickLine] = useState<ExtendedPickingLine | null>(null);

  // Batch number registration modal
  const [showBatchNumberModal, setShowBatchNumberModal] = useState(false);
  const [batchNumberLine, setBatchNumberLine] = useState<ExtendedPickingLine | null>(null);

  // Add additional line modal
  const [showAddLineModal, setShowAddLineModal] = useState(false);

  // Global barcode scanner - works without input focus
  // Automatically disabled when modals are open or when setting is off
  const barcodeScannerEnabled = useBarcodeScannerEnabled();
  const isAnyModalOpen = showPalletManager || showImageModal || showSettingsModal ||
                         showPrintModal || showLocationScan || showLocationOverride ||
                         showSplitModal || showQuantityDistribution || showDeliveryInfoModal ||
                         showQuantityConfirm || showQuantityDeviation || showSignatureModal ||
                         showMandatoryCounting || showZeroPointCounting || showPackageScan ||
                         showSerialNumberModal || showSerialPickModal || showBatchPickModal || showBatchNumberModal || showAddLineModal ||
                         showPackZoneSelection;

  useSimpleBarcodeScanner({
    enabled: barcodeScannerEnabled && !isAnyModalOpen,
    onScan: (barcode: string) => {
      console.log('🌍 Global scan detected:', barcode);

      // Process the barcode using the same logic as GS1Scanner
      processGlobalBarcode(barcode);
    }
  });

  const processGlobalBarcode = (scanInput: string) => {
    console.log('🔍 Processing global barcode:', scanInput);

    // First, remove trailing scanner separators to check for real GS1 structure
    const withoutTrailingSeparators = scanInput.replace(/[~§]+$/g, '').trim();

    // Check if this looks like a GS1-128 structured barcode
    const hasInternalSeparators = withoutTrailingSeparators.includes('~') ||
                                   withoutTrailingSeparators.includes('§') ||
                                   withoutTrailingSeparators.includes(String.fromCharCode(29));
    const hasParentheses = scanInput.includes('(');
    const hasC1Prefix = scanInput.startsWith(']C1');

    const looksLikeGS1 = hasInternalSeparators || hasParentheses || hasC1Prefix;

    let fullGS1Data = null;
    let hasGS1Data = false;

    if (looksLikeGS1) {
      console.log('🔍 GS1 structure detected, attempting GS1 parse...');
      fullGS1Data = parseGS1BarcodeFull(scanInput);
      hasGS1Data = !!(fullGS1Data.parsedAIs && Object.keys(fullGS1Data.parsedAIs).length > 0);
      console.log('📊 GS1 parse result - hasGS1Data:', hasGS1Data, 'parsedAIs:', fullGS1Data?.parsedAIs);
    } else {
      console.log('📝 No GS1 structure detected, treating as plain barcode');
    }

    if (hasGS1Data && fullGS1Data) {
      console.log('📊 Global scan: Detected GS1-128 barcode');

      const gs1Data: GS1ScanData = {
        sscc: fullGS1Data.sscc,
        gtin: fullGS1Data.gtin,
        itemNo: fullGS1Data.gtin,
        quantity: fullGS1Data.quantity,
        batchNo: fullGS1Data.batchNo,
        expiryDate: fullGS1Data.expiryDate || fullGS1Data.bestBeforeDate || fullGS1Data.useByDate
      };

      console.log('📋 Global scan: Extracted GS1 data:', gs1Data);

      const hasSSCC = !!gs1Data.sscc;
      const hasItem = !!(gs1Data.itemNo || gs1Data.gtin);

      // If no relevant GS1 data, treat as plain barcode
      if (!hasSSCC && !hasItem) {
        console.log('⚠️ GS1 detected but no SSCC/GTIN found - treating as plain barcode');
        const cleanedItemNo = scanInput.replace(/[~§]+$/g, '').trim();
        handleScan({ itemNo: cleanedItemNo });
      }
      // Pre-filled pallet scan (SSCC + Item)
      else if (hasSSCC && hasItem) {
        console.log('📦 Global scan: Pre-filled pallet scan detected (SSCC + Item)');
        handleScan(gs1Data);
      }
      // SSCC only - pallet management
      else if (hasSSCC && !hasItem) {
        console.log('📦 Global scan: SSCC-only scan - pallet management');
        let targetPallet = pallets.find(p => p.sscc === gs1Data.sscc);

        if (!targetPallet) {
          console.log('➕ Creating empty pallet with SSCC:', gs1Data.sscc);
          createEmptyPallet(gs1Data.sscc);
        } else {
          console.log('✅ Activating existing pallet:', targetPallet.sscc);
          setActivePalletSSCC(targetPallet.sscc);
        }
      }
      // Item only
      else if (!hasSSCC && hasItem) {
        console.log('🔎 Global scan: Item-only scan');
        handleScan(gs1Data);
      }
    } else {
      // Plain barcode
      console.log('📝 Global scan: Plain barcode (not GS1-128)');
      const cleanedItemNo = scanInput.replace(/[~§]+$/g, '').trim();
      console.log('🧹 Cleaned plain barcode:', cleanedItemNo);
      handleScan({ itemNo: cleanedItemNo });
    }
  };

  const handleReorder = async (oldIndex: number, newIndex: number) => {
    // Import arrayMove from @dnd-kit/sortable
    const { arrayMove } = await import('@dnd-kit/sortable');

    // Update local state immediately (optimistic update)
    const reorderedLines = arrayMove(pickingLines, oldIndex, newIndex);
    setPickingLines(reorderedLines);

    // Save to backend
    try {
      await pickingService.updateLineOrder(
        pListId,
        reorderedLines.map((line, index) => ({
          pListLineNo: line.pListLineNo,
          sortOrder: index + 1
        }))
      );
    } catch (error) {
      console.error('Error saving line order:', error);
      // Revert on error
      await loadPickingDetails();
    }
  };

  // Auto-show image panel when active line changes and has an image (if enabled)
  useEffect(() => {
    // Only process if we have valid data and auto-show is enabled
    if (!autoShowImage || pickingLines.length === 0 || activeLineIndex === null) {
      if (!autoShowImage) {
        setShowImagePanel(false);
      }
      return;
    }

    const currentActiveLine = pickingLines[activeLineIndex];
    if (currentActiveLine?.image) {
      setShowImagePanel(true);
      setCurrentLineIndex(activeLineIndex);
    } else {
      setShowImagePanel(false);
    }
  }, [activeLineIndex, pickingLines, autoShowImage]);

  // Browser refresh warning - prevent loss of empty pallets
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (emptyPallets.length > 0) {
        e.preventDefault();
        e.returnValue = 'Du har tomme paller som ikke er lagret. Disse vil forsvinne hvis du forlater siden.';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [emptyPallets]);

  // Handle line selection from list
  const handleLineSelect = (index: number) => {
    setActiveLineIndex(index);
    setCurrentLineIndex(index);

    const line = pickingLines[index];
    if (line?.image && autoShowImage) {
      setShowImagePanel(true);
    }
  };

  // Handle manual image show/hide button click (toggle)
  const handleShowImage = () => {
    if (activeLine?.image) {
      if (showImagePanel) {
        // If image is already showing, close it
        setShowImagePanel(false);
      } else {
        // If image is not showing, open it
        setShowImagePanel(true);
        setCurrentLineIndex(activeLineIndex);
      }
    }
  };

  /**
   * Helper function to show quantity confirmation with package scan check.
   * For batch picks with packageNo, shows PackageScanModal first.
   * Otherwise shows QuantityConfirmModal directly.
   */
  const showQuantityConfirmWithPackageCheck = (line: ExtendedPickingLine, quantity: number) => {
    setLineToConfirm(line);
    setQuantityToConfirm(quantity);

    // Check if this is a batch pick AND line has a package assigned
    const isBatchPick = pickingDetails?.header.pListIsBatchYesNo === true;
    const hasPackage = line.packageNo != null && line.packageNo > 0;

    if (isBatchPick && hasPackage) {
      // Show package scan modal first
      setShowPackageScan(true);
    } else {
      // Show quantity confirm directly
      setShowQuantityConfirm(true);
    }
  };

  /**
   * Handler when package scan is confirmed - proceed to quantity confirmation
   */
  const handlePackageScanConfirm = () => {
    setShowPackageScan(false);
    setShowQuantityConfirm(true);
  };

  const handleScan = async (scanData: GS1ScanData) => {
    console.log('🔍 ModernPickingDetails: Processing scan with full GS1 data', scanData);

    const { itemNo, quantity, sscc, batchNo, expiryDate, gtin } = scanData;

    console.log('🔍 ModernPickingDetails: Destructured - itemNo=', itemNo, 'gtin=', gtin, 'sscc=', sscc);
    console.log('🔍 ModernPickingDetails: effectiveItemNo will be:', itemNo || gtin);

    // SCENARIO 1: SSCC-only scan (no item) - handled in GS1Scanner, shouldn't reach here
    if (sscc && !itemNo && !gtin) {
      console.log('⚠️ SSCC-only scan should be handled in GS1Scanner');
      return;
    }

    // SCENARIO 2: Pre-filled pallet scan (SSCC + Item in same barcode)
    // Store SSCC info and continue to location/quantity validation
    // The pallet will be created when user confirms quantity
    if (sscc && (itemNo || gtin)) {
      console.log('📦 Pre-filled pallet detected - storing SSCC for later pallet creation');
      // Store the full GS1 data for use after quantity confirmation
      setPendingGS1Data(scanData);

      // Check if pallet already exists
      let targetPallet = pallets.find(p => p.sscc === sscc);
      if (targetPallet) {
        console.log('✅ Pallet already exists, setting as active');
        setActivePalletSSCC(sscc);
      }
      // Continue to location/quantity validation below
      // The SSCC info in scanData will be used when quantity is confirmed
    }

    // Use GTIN if itemNo is not present
    const effectiveItemNo = itemNo || gtin;
    if (!effectiveItemNo) {
      console.log('⚠️ No item identifier found in scan');
      return;
    }

    // Helper function to check if scanned item matches line
    const itemsMatch = (scannedItem: string, line: ExtendedPickingLine): boolean => {
      const scannedLower = scannedItem.toLowerCase();
      const cleanedScanned = scannedItem.replace(/^0+/, '') || scannedItem;
      const cleanedScannedLower = cleanedScanned.toLowerCase();

      console.log(`🔎 Matching: scanned="${scannedItem}" (cleaned="${cleanedScanned}") against line.itemNo="${line.itemNo}", line.itemBarCode="${line.itemBarCode}"`);

      // Match against itemNo
      if (line.itemNo) {
        const itemNoLower = line.itemNo.toLowerCase();
        if (itemNoLower === scannedLower || itemNoLower === cleanedScannedLower) {
          console.log(`✅ MATCH on itemNo: ${line.itemNo}`);
          return true;
        }
      }

      // Match against itemBarCode
      if (line.itemBarCode) {
        const barCodeLower = line.itemBarCode.toLowerCase();
        if (barCodeLower === scannedLower || barCodeLower === cleanedScannedLower) {
          console.log(`✅ MATCH on itemBarCode: ${line.itemBarCode}`);
          return true;
        }
      }

      console.log(`❌ NO MATCH for this line`);
      return false;
    };

    // RAPID SCANNING MODE: Skip all dialogs and directly update quantity
    if (enableRapidScanning) {
      console.log('⚡ Rapid scanning mode enabled');

      // Find matching line (prefer incomplete lines)
      const matchedLine = findLineByItem(effectiveItemNo);

      if (!matchedLine) {
        // Show error but don't block - just focus back on scanner
        console.error('❌ Item not found in picking list:', effectiveItemNo);
        alert('Vare ikke funnet i plukklisten!');
        return;
      }

      // Set matched line as active
      const lineIndex = pickingLines.indexOf(matchedLine);
      setCurrentLineIndex(lineIndex);
      setActiveLineIndex(lineIndex);

      // Calculate new quantity (increment by 1)
      const newTotalQuantity = matchedLine.qtyPicked + 1;

      // Validate that we don't exceed ordered quantity
      if (newTotalQuantity > matchedLine.qty) {
        alert(`Maksimalt antall nådd (${matchedLine.qty})`);
        return;
      }

      // Store pending GS1 data for pallet creation
      if (sscc && (itemNo || gtin)) {
        setPendingGS1Data(scanData);

        // Create/activate pallet if needed
        let targetPallet = pallets.find(p => p.sscc === sscc);
        if (!targetPallet) {
          await createNewPallet(sscc, scanData);
        } else {
          setActivePalletSSCC(sscc);
        }
      }

      // Directly update quantity without any dialogs
      try {
        await handleUpdateQuantity(matchedLine.pListLineNo, newTotalQuantity);
        console.log('✅ Rapid scan completed successfully');
      } catch (error) {
        console.error('Error in rapid scan update:', error);
        alert('Kunne ikke oppdatere antall. Prøv igjen.');
      }

      return;
    }

    // NORMAL MODE: Original logic with dialogs
    // Check if scanned item matches active line (case-insensitive with GTIN cleaning)
    if (activeLine && itemsMatch(effectiveItemNo, activeLine)) {
      const activeIndex = activeLineIndex!;
      setCurrentLineIndex(activeIndex); // Set visual selection

      if (activeLine.image && autoShowImage) {
        setShowImagePanel(true);
      }

      const quantityToAdd = quantity || (activeLine.qty - activeLine.qtyPicked);

      // TODO: If we have batch/expiry from GS1, we should create OrderSSCC record here too
      console.log('Scanned item data:', { batchNo, expiryDate, gtin });

      if (activeLine.requireLocationScan) {
        setShowLocationScan(true);
        setTempGS1Quantity(quantityToAdd);
      } else {
        // Show quantity confirmation modal (with package scan check for batch picks)
        showQuantityConfirmWithPackageCheck(activeLine, quantityToAdd);
      }
    } else {
      // Fallback to original logic for non-active line scanning
      const matchedLine = findLineByItem(effectiveItemNo);

      if (matchedLine) {
        const lineIndex = pickingLines.indexOf(matchedLine);
        setCurrentLineIndex(lineIndex); // Set visual selection
        setActiveLineIndex(lineIndex); // Override active line

        if (matchedLine.image && autoShowImage) {
          setShowImagePanel(true);
        }

        const quantityToAdd = quantity || (matchedLine.qty - matchedLine.qtyPicked);

        if (matchedLine.requireLocationScan) {
          setShowLocationScan(true);
          setTempGS1Quantity(quantityToAdd);
        } else {
          // Show quantity confirmation modal (with package scan check for batch picks)
          showQuantityConfirmWithPackageCheck(matchedLine, quantityToAdd);
        }
      } else {
        alert('Vare ikke funnet i plukklisten!');
      }
    }
  };

  const handleMandatoryCountingSubmit = async (countedQuantity: number) => {
    if (!currentLine || !pickingDetails || !mandatoryCountingData) return;

    setIsSubmittingCounting(true);
    try {
      const companyNo = pickingDetails.header.companyNo;
      await mandatoryCountingService.submitMandatoryCounting(companyNo, {
        warehouseID: currentLine.warehouseID,
        itemNo: mandatoryCountingData.itemNo,
        binCode: mandatoryCountingData.binCode,
        countedQuantity: countedQuantity,
        pListID: pListId,
        pListLineNo: currentLine.pListLineNo
      });

      // Close mandatory counting modal
      setShowMandatoryCounting(false);
      setMandatoryCountingData(null);

      // Continue with normal picking flow (quantity confirmation with package scan check)
      const quantityToAdd = tempGS1Quantity !== null ? tempGS1Quantity : (currentLine.qty - currentLine.qtyPicked);
      showQuantityConfirmWithPackageCheck(currentLine, quantityToAdd);
      setTempGS1Quantity(null);
    } catch (error) {
      console.error('Error submitting mandatory counting:', error);
      alert('Kunne ikke registrere telling. Prøv igjen.');
    } finally {
      setIsSubmittingCounting(false);
    }
  };

  const handleLocationScanConfirm = async () => {
    if (!currentLine || !pickingDetails) return;

    if (locationScan.toLowerCase() === currentLine.location?.toLowerCase() || overrideLocation) {
      // Check for mandatory counting requirement
      try {
        const companyNo = pickingDetails.header.companyNo;
        const checkResult = await mandatoryCountingService.checkMandatoryCounting(companyNo, {
          warehouseId: currentLine.warehouseID,
          itemNo: currentLine.itemNo || '',
          binCode: currentLine.location || ''
        });

        if (checkResult.requiresCounting) {
          // Block picking flow and show mandatory counting modal
          setShowLocationScan(false);
          setLocationScan('');
          setOverrideLocation('');

          setMandatoryCountingData({
            itemNo: currentLine.itemNo || '',
            itemDescription: currentLine.itemDescription || currentLine.itemNo || '',
            binCode: currentLine.location || '',
            lastCountedDate: checkResult.lastCountedDate,
            daysSinceLastCounted: checkResult.daysSinceLastCounted,
            currentQuantity: checkResult.currentQuantity
          });
          setShowMandatoryCounting(true);
          return; // Block until counting is completed
        }
      } catch (error) {
        console.error('Error checking mandatory counting:', error);
        // Continue with picking if check fails (don't block operations)
      }

      // Normal flow - no mandatory counting required
      setShowLocationScan(false);
      setLocationScan('');
      setOverrideLocation('');

      const quantityToAdd = tempGS1Quantity !== null ? tempGS1Quantity : (currentLine.qty - currentLine.qtyPicked);

      // Show quantity confirmation modal (with package scan check for batch picks)
      showQuantityConfirmWithPackageCheck(currentLine, quantityToAdd);
      setTempGS1Quantity(null);
    } else {
      alert(`Feil lokasjon! Forventet: ${currentLine.location}, Skannet: ${locationScan}`);
    }
  };

  const handleLocationOverride = async () => {
    setShowLocationOverride(false);
    setShowLocationScan(false);

    if (currentLine && overrideLocation) {
      try {
        // Update BinCode in database
        await pickingService.updateBinCode(pListId, currentLine.pListLineNo, overrideLocation);

        // Update the location on the picking line
        const updatedLines = pickingLines.map(l =>
          l.pListLineNo === currentLine.pListLineNo ? { ...l, location: overrideLocation } : l
        );
        setPickingLines(updatedLines);

        // Update currentLine reference with the new location
        const updatedCurrentLine = { ...currentLine, location: overrideLocation };

        const quantityToAdd = tempGS1Quantity !== null ? tempGS1Quantity : (currentLine.qty - currentLine.qtyPicked);

        // Show quantity confirmation modal (with package scan check for batch picks)
        showQuantityConfirmWithPackageCheck(updatedCurrentLine, quantityToAdd);
        setTempGS1Quantity(null);
      } catch (error) {
        console.error('Error updating location override:', error);
        alert('Kunne ikke oppdatere lokasjon. Prøv igjen.');
      }
    }
    setOverrideLocation('');
  };

  // Check if few-point/zero-point counting should trigger after pick
  const checkIfFewPointCounting = async (
    itemNo: string,
    binCode: string,
    pickedQuantity: number
  ): Promise<{ shouldCount: boolean; remainingAfterPick: number }> => {
    if (!itemNo || !binCode) return { shouldCount: false, remainingAfterPick: 0 };

    try {
      // Get current ItemBins quantity for this item at this location
      const itemBins = await dataInspectionService.getItemBinsByItemNo(itemNo);

      // Find the specific bin
      const binData = itemBins.find(
        (bin) => bin.binCode === binCode
      );

      if (!binData) {
        console.log('⚠️ No ItemBins data found for', itemNo, 'at', binCode);
        return { shouldCount: false, remainingAfterPick: 0 };
      }

      // Check if remaining quantity after pick is at or below the threshold
      // threshold=0: triggers only when location becomes empty (legacy zero-point)
      // threshold=2: triggers when 2 or fewer items remain after pick
      const availableQty = binData.quantityAvailable || 0;
      const remainingAfterPick = Math.max(0, availableQty - pickedQuantity);
      const shouldCount = remainingAfterPick <= fewPointCountingThreshold;

      console.log('🔍 Few-point counting check:', {
        itemNo,
        binCode,
        availableQty,
        pickedQuantity,
        remainingAfterPick,
        threshold: fewPointCountingThreshold,
        shouldCount
      });

      return { shouldCount, remainingAfterPick };
    } catch (error) {
      console.error('Error checking few-point counting:', error);
      return { shouldCount: false, remainingAfterPick: 0 };
    }
  };

  // Handle zero-point counting - confirm zero
  const handleZeroPointConfirmZero = async () => {
    if (!zeroPointCountingData || !pickingDetails) return;

    setIsSubmittingZeroPoint(true);
    try {
      // Register count of 0 via mandatoryCountingService (same as mandatory counting)
      const companyNo = pickingDetails.header.companyNo;
      await mandatoryCountingService.submitMandatoryCounting(companyNo, {
        warehouseID: zeroPointCountingData.warehouse,
        itemNo: zeroPointCountingData.itemNo,
        binCode: zeroPointCountingData.binCode,
        countedQuantity: 0, // Zero-point counting confirmed
        pListID: pListId,
        pListLineNo: zeroPointCountingData.pListLineNo
      });

      console.log('✅ Zero-point count registered for', zeroPointCountingData.binCode);

      // Close modal
      setShowZeroPointCounting(false);
      setZeroPointCountingData(null);
    } catch (error) {
      console.error('Error registering zero-point count:', error);
      alert('Kunne ikke registrere nullpunktstelling. Prøv igjen.');
    } finally {
      setIsSubmittingZeroPoint(false);
    }
  };

  // Handle zero-point counting - submit deviation
  const handleZeroPointSubmitDeviation = async (countedQuantity: number) => {
    if (!zeroPointCountingData || !pickingDetails) return;

    setIsSubmittingZeroPoint(true);
    try {
      // Register count with deviation via mandatoryCountingService (same as mandatory counting)
      const companyNo = pickingDetails.header.companyNo;
      await mandatoryCountingService.submitMandatoryCounting(companyNo, {
        warehouseID: zeroPointCountingData.warehouse,
        itemNo: zeroPointCountingData.itemNo,
        binCode: zeroPointCountingData.binCode,
        countedQuantity: countedQuantity, // Actual counted quantity (deviation from expected 0)
        pListID: pListId,
        pListLineNo: zeroPointCountingData.pListLineNo
      });

      console.log('✅ Zero-point deviation registered:', countedQuantity, 'at', zeroPointCountingData.binCode);

      // Close modal
      setShowZeroPointCounting(false);
      setZeroPointCountingData(null);
    } catch (error) {
      console.error('Error registering zero-point deviation:', error);
      alert('Kunne ikke registrere avvik. Prøv igjen.');
    } finally {
      setIsSubmittingZeroPoint(false);
    }
  };

  const handleQuantityConfirm = async (confirmedQuantity: number) => {
    if (!lineToConfirm) return;

    const newTotalQuantity = lineToConfirm.qtyPicked + confirmedQuantity;

    // If we have pending GS1 data with SSCC, create/activate pallet first
    if (pendingGS1Data?.sscc) {
      console.log('📦 Creating/activating pallet from pending GS1 data:', pendingGS1Data.sscc);

      // Check if pallet already exists
      let targetPallet = pallets.find(p => p.sscc === pendingGS1Data.sscc);
      if (!targetPallet) {
        // Create new pallet with GS1 data
        await createNewPallet(pendingGS1Data.sscc, pendingGS1Data);
      } else {
        // Activate existing pallet
        setActivePalletSSCC(pendingGS1Data.sscc);
      }
    }

    // Check if few-point counting should trigger (only if zero-point/few-point counting is enabled)
    const fewPointResult = zeroPointCountingEnabled && lineToConfirm.itemNo && lineToConfirm.location
      ? await checkIfFewPointCounting(
          lineToConfirm.itemNo,
          lineToConfirm.location,
          confirmedQuantity
        )
      : { shouldCount: false, remainingAfterPick: 0 };

    await handleUpdateQuantity(lineToConfirm.pListLineNo, newTotalQuantity);

    setShowQuantityConfirm(false);
    setLineToConfirm(null);
    setQuantityToConfirm(0);

    // Clear pending GS1 data after processing
    setPendingGS1Data(null);

    // If few-point/zero-point counting triggered, show counting modal
    if (fewPointResult.shouldCount && lineToConfirm.warehouseID) {
      setZeroPointCountingData({
        itemNo: lineToConfirm.itemNo!,
        itemDescription: lineToConfirm.itemDescription || '',
        binCode: lineToConfirm.location || '',
        pickedQuantity: confirmedQuantity,
        expectedRemaining: fewPointResult.remainingAfterPick,
        warehouse: lineToConfirm.warehouseID,
        pListLineNo: lineToConfirm.pListLineNo
      });
      setShowZeroPointCounting(true);
    }
  };

  const handlePickLine = (lineNo: number, qty: number) => {
    const line = pickingLines.find(l => l.pListLineNo === lineNo);
    if (!line) return;

    // ItemType=6: Serial-tracked items in inventory - show serial pick modal
    if (line.itemType === 6 && line.qtyPicked < line.qty) {
      setSerialPickLine(line);
      setShowSerialPickModal(true);
      return;
    }

    // ItemType=4/5: Batch items - show batch pick modal
    if ((line.itemType === 4 || line.itemType === 5) && line.qtyPicked < line.qty) {
      setBatchPickLine(line);
      setShowBatchPickModal(true);
      return;
    }

    const quantityToAdd = qty - line.qtyPicked;
    // Show quantity confirmation modal (with package scan check for batch picks)
    showQuantityConfirmWithPackageCheck(line, quantityToAdd);
  };

  const handleUpdateQuantity = async (lineNo: number, newTotalQty: number) => {
    console.log('🔄 Updating quantity for line:', lineNo, 'newTotalQty:', newTotalQty);

    const line = pickingLines.find(l => l.pListLineNo === lineNo);
    if (!line) return;

    // Validate that newTotalQty does not exceed ordered quantity
    if (newTotalQty > line.qty) {
      alert(`Antall kan ikke overstige bestilt antall (${line.qty})`);
      return;
    }

    try {
      // Get StartDateTime for this line from state
      const startDateTime = lineStartTimes.get(lineNo);

      // Update picked quantity in PListLine - send the new total quantity and StartDateTime
      await pickingService.updatePickedQuantity(pListId, lineNo, newTotalQty, undefined, startDateTime);

      // Check if active pallet is empty (frontend-only) - will be converted
      const isConvertingEmptyPallet = activePallet && isEmptyPallet(activePallet);

      if (isConvertingEmptyPallet) {
        console.log('🎉 Converting empty pallet to database pallet:', activePallet.sscc);
      }

      // If there's an active pallet and new quantity > 0, save to OrderSSCC
      if (activePallet && newTotalQty > 0) {
        const line = pickingLines.find(l => l.pListLineNo === lineNo);
        if (line) {
          const quantityForPallet = newTotalQty - line.qtyPicked; // Only the new quantity for this operation
          if (quantityForPallet > 0) {
            // Use batch/expiry data from pendingGS1Data if available
            const batchNo = pendingGS1Data?.batchNo || 'DEFAULT';
            const expiryDate = pendingGS1Data?.expiryDate || null;

            console.log('📋 Creating OrderSSCC with batch/expiry:', { batchNo, expiryDate });

            const ssccRequest: CreateOrderSSCCRequest = {
              companyNo: '', // Will be set by backend
              pListID: pListId,
              orderNo: line.orderNo,
              lineNo: lineNo,
              sscc: activePallet.sscc,
              gtin: line.itemNo,
              quantity: quantityForPallet,
              ssccLevel: 1,
              itemBatchNo: batchNo,
              itemBatchExpDate: expiryDate,
              stackingWeight: 0,
              grossWeight: 0,
              locationName: line.location
            };

            await pickingService.createOrUpdateSSCC(pListId, ssccRequest);
          }
        }
      }

      // Reload pallets from database to get updated data (will convert empty pallet automatically)
      await loadPallets();
      
      // Update local state
      const updatedLines = pickingLines.map(line => {
        if (line.pListLineNo === lineNo) {
          const isFullyPicked = newTotalQty >= line.qty;
          const isPartiallyPicked = newTotalQty > 0 && newTotalQty < line.qty;
          
          return {
            ...line,
            qtyPicked: newTotalQty,
            isCompleted: newTotalQty > 0,
            isPartiallyPicked,
            isFullyPicked,
            palletId: activePallet?.sscc
          };
        }
        return line;
      });
      
      // Find the line that was just updated
      const lineIndex = updatedLines.findIndex(l => l.pListLineNo === lineNo);
      const line = updatedLines[lineIndex];
      
      if (lineIndex !== -1 && line && line.isFullyPicked && lineIndex === activeLineIndex) {
        // Line is fully picked and is the active line - reorganize to move it to bottom
        setTimeout(() => {
          const reorganized = reorganizeLines(updatedLines);
          setPickingLines(reorganized);

          // Force activeLineIndex to change to trigger useEffect
          // First set to null, then to 0 in next tick
          setActiveLineIndex(null);
          setTimeout(() => setActiveLineIndex(0), 0);
        }, 300);
      } else if (lineIndex !== -1 && line && !line.isFullyPicked) {
        // Partially picked - keep as active, reorganize if needed
        const reorganized = reorganizeLines(updatedLines);
        setPickingLines(reorganized);
        setActiveLineIndex(0);
        setCurrentLineIndex(0);
      } else {
        // Just update the lines and reorganize
        const reorganized = reorganizeLines(updatedLines);
        setPickingLines(reorganized);
      }
    } catch (error) {
      console.error('Error updating quantity:', error);
      alert('Kunne ikke oppdatere antall. Prøv igjen.');
    }
  };

  /**
   * Handle serial number picked from SerialPickModal (ItemType=6).
   * Increments QtyPicked by 1 and passes the selected serialNo to the backend.
   */
  const handleSerialPicked = async (serialNo: string) => {
    if (!serialPickLine) return;

    const lineNo = serialPickLine.pListLineNo;
    const newTotalQty = serialPickLine.qtyPicked + 1;

    try {
      const startDateTime = lineStartTimes.get(lineNo);

      await pickingService.updatePickedQuantity(
        pListId, lineNo, newTotalQty, undefined, startDateTime, serialNo
      );

      // Also create OrderSerialNo record for ERP export file
      await pickingService.createSerialNumber(
        pListId, serialPickLine.orderNo, serialPickLine.lineNo, serialNo
      );

      // Close modal
      setShowSerialPickModal(false);
      setSerialPickLine(null);

      // Reload pallets
      await loadPallets();

      // Update local state
      const updatedLines = pickingLines.map(l => {
        if (l.pListLineNo === lineNo) {
          const isFullyPicked = newTotalQty >= l.qty;
          const isPartiallyPicked = newTotalQty > 0 && newTotalQty < l.qty;
          return {
            ...l,
            qtyPicked: newTotalQty,
            isCompleted: newTotalQty > 0,
            isPartiallyPicked,
            isFullyPicked,
            palletId: activePallet?.sscc
          };
        }
        return l;
      });

      const reorganized = reorganizeLines(updatedLines);
      setPickingLines(reorganized);
      setActiveLineIndex(0);
    } catch (error: any) {
      console.error('Error picking serial:', error);
      alert(error.message || 'Kunne ikke registrere serienummer-plukk. Prøv igjen.');
    }
  };

  /**
   * Handle batch picked from BatchPickModal (ItemType=4/5).
   * Updates QtyPicked by the selected quantity and registers the batch via OrderBatchNo.
   */
  const handleBatchPicked = async (batchNo: string, quantity: number) => {
    if (!batchPickLine) return;

    const lineNo = batchPickLine.pListLineNo;
    const newTotalQty = batchPickLine.qtyPicked + quantity;

    try {
      const startDateTime = lineStartTimes.get(lineNo);

      await pickingService.updatePickedQuantity(
        pListId, lineNo, newTotalQty, undefined, startDateTime
      );

      // Create OrderBatchNo record for ERP export file
      await pickingService.createBatchNumber(
        pListId,
        batchPickLine.orderNo,
        batchPickLine.lineNo,
        batchPickLine.pListLineNo,
        batchNo,
        quantity
      );

      // Close modal
      setShowBatchPickModal(false);
      setBatchPickLine(null);

      // Reload pallets
      await loadPallets();

      // Update local state
      const updatedLines = pickingLines.map(l => {
        if (l.pListLineNo === lineNo) {
          const isFullyPicked = newTotalQty >= l.qty;
          const isPartiallyPicked = newTotalQty > 0 && newTotalQty < l.qty;
          return {
            ...l,
            qtyPicked: newTotalQty,
            isCompleted: newTotalQty > 0,
            isPartiallyPicked,
            isFullyPicked,
            palletId: activePallet?.sscc
          };
        }
        return l;
      });

      const reorganized = reorganizeLines(updatedLines);
      setPickingLines(reorganized);
      setActiveLineIndex(0);
    } catch (error: any) {
      console.error('Error picking batch:', error);
      alert(error.message || 'Kunne ikke registrere batch-plukk. Prøv igjen.');
    }
  };

  const createNewPallet = async (ssccCode?: string, gs1Data?: GS1ScanData) => {
    // SCENARIO 1: Pre-filled pallet (GS1 with item data)
    if (gs1Data && (gs1Data.gtin || gs1Data.itemNo)) {
      const matchedLine = findLineByItem(gs1Data.gtin || gs1Data.itemNo!);

      if (!matchedLine) {
        alert(`Ingen plukklinje matcher GTIN: ${gs1Data.gtin || gs1Data.itemNo}`);
        return;
      }

      // Generate SSCC if not provided
      const finalSSCC = ssccCode || `PALL-${Date.now()}`;

      try {
        // Create OrderSSCC with REAL data from GS1
        const orderSSCCRequest: CreateOrderSSCCRequest = {
          companyNo: pickingDetails!.header.companyNo,
          pListID: pListId,
          orderNo: matchedLine.orderNo,
          lineNo: matchedLine.lineNo,
          sscc: finalSSCC,
          gtin: gs1Data.gtin || gs1Data.itemNo,
          quantity: gs1Data.quantity || matchedLine.qty,
          ssccLevel: 1,
          itemBatchNo: gs1Data.batchNo || 'NO_BATCH',
          itemBatchExpDate: gs1Data.expiryDate || null,
          locationName: matchedLine.binCode
        };

        await pickingService.createOrUpdateSSCC(pListId, orderSSCCRequest);

        // Update picked quantity
        await handleUpdateQuantity(
          matchedLine.pListLineNo,
          matchedLine.qtyPicked + (gs1Data.quantity || matchedLine.qty)
        );

        // Reload from database
        await loadPallets();
        setActivePalletSSCC(finalSSCC);
        alert(`Pall ${finalSSCC} opprettet med vare!`);
        return;
      } catch (error) {
        console.error('Error creating pre-filled pallet:', error);
        alert('Kunne ikke opprette pall. Prøv igjen.');
        return;
      }
    }

    // SCENARIO 2: Empty pallet (no item data)
    // ✅ NEW: Create frontend-only empty pallet (NO DATABASE CALL!)
    createEmptyPallet(ssccCode);
    console.log('✅ Empty pallet created (frontend-only, no database)');
  };

  const setActivePallet = (sscc: string) => {
    setActivePalletSSCC(sscc);
  };

  const deletePallet = async (sscc: string) => {
    try {
      const palletToDelete = pallets.find(p => p.sscc === sscc);
      if (!palletToDelete) return;

      // Check if empty frontend-only pallet
      if (isEmptyPallet(palletToDelete)) {
        console.log('🗑️ Deleting empty frontend-only pallet:', sscc);
        deleteEmptyPallet(sscc);
        console.log('✅ Empty pallet deleted (no database call)');
        return;
      }

      // Database pallet - delete all OrderSSCC records
      const records = await pickingService.getSSCCContents(sscc);

      for (const record of records) {
        await pickingService.deleteSSCC(pListId, record.id);
      }

      // Reload pallets
      await loadPallets();

      console.log('✅ Database pallet deleted:', sscc);
    } catch (error) {
      console.error('Error deleting pallet:', error);
      alert('Kunne ikke slette pall. Prøv igjen.');
    }
  };

  const handleLocationEdit = async (lineNo: number, location: string) => {
    // Guard against duplicate calls - only process if this line is currently being edited
    if (editingLocation !== lineNo) {
      return;
    }

    // Immediately reset editing state to prevent duplicate calls
    setEditingLocation(null);
    setNewLocation('');

    const line = pickingLines.find(l => l.pListLineNo === lineNo);
    if (!line) {
      return;
    }

    // Validate location exists in database (case-insensitive)
    try {
      const locationExists = await stcBinService.exists(
        pickingDetails!.header.companyNo,
        line.warehouseID,
        location.toLowerCase()
      );

      if (!locationExists) {
        alert(`Lokasjon "${location}" finnes ikke i lageret. Vennligst oppgi en gyldig lokasjon.`);
        return;
      }

      // Validate that item has sufficient inventory on the location
      const itemBins = await dataInspectionService.getItemBinsByBinCode(location.toLowerCase());
      const itemOnLocation = itemBins.find(ib => ib.itemNo.toLowerCase() === line.itemNo?.toLowerCase());

      const requiredQuantity = line.qty - line.qtyPicked;
      if (!itemOnLocation || itemOnLocation.quantityAvailable < requiredQuantity) {
        const availableQty = itemOnLocation?.quantityAvailable || 0;
        alert(`Vare "${line.itemNo}" har kun ${availableQty} tilgjengelig på lokasjon "${location}", men ${requiredQuantity} er påkrevd`);
        return;
      }

      // Location and inventory are valid, update in database
      await pickingService.updateBinCode(pListId, lineNo, location);

      // Update local state
      const updatedLines = pickingLines.map(l =>
        l.pListLineNo === lineNo ? { ...l, location } : l
      );
      setPickingLines(updatedLines);
    } catch (error) {
      console.error('Error validating location:', error);
      alert('Kunne ikke validere lokasjon. Prøv igjen.');
    }
  };

  const handleQuantityEdit = async (lineNo: number, newQty: number) => {
    const line = pickingLines.find(l => l.pListLineNo === lineNo);
    if (!line) return;

    // Validate that newQty does not exceed ordered quantity
    if (newQty > line.qty) {
      alert(`Antall kan ikke overstige bestilt antall (${line.qty})`);
      setEditingQuantity(null);
      setNewQuantity('');
      return;
    }

    try {
      // Get StartDateTime for this line from state
      const startDateTime = lineStartTimes.get(lineNo);

      await pickingService.updatePickedQuantity(pListId, lineNo, newQty, undefined, startDateTime);
      
      const updatedLines = pickingLines.map(l => {
        if (l.pListLineNo === lineNo) {
          const isFullyPicked = newQty >= l.qty;
          const isPartiallyPicked = newQty > 0 && newQty < l.qty;
          
          return {
            ...l,
            qtyPicked: newQty,
            isCompleted: newQty > 0,
            isPartiallyPicked,
            isFullyPicked
          };
        }
        return l;
      });

      // Reorganize lines
      const reorganized = reorganizeLines(updatedLines);
      setPickingLines(reorganized);
      setActiveLineIndex(0);
      
      setEditingQuantity(null);
      setNewQuantity('');

      // Reload pallets from database to get updated data
      await loadPallets();
    } catch (error) {
      console.error('Error updating quantity:', error);
      alert('Kunne ikke oppdatere antall. Prøv igjen.');
    }
  };

  const handleUndoPicking = async (lineNo: number) => {
    // Find the line to get item info for the confirmation message
    const line = pickingLines.find(l => l.pListLineNo === lineNo);
    const lineInfo = line
      ? `${line.itemNo} - ${line.itemDescription || 'Ukjent vare'} (Antall: ${line.qtyPicked})`
      : `Linje ${lineNo}`;

    // Show confirmation dialog
    const confirmed = window.confirm(
      `Er du sikker på at du vil angre plukkingen av denne linjen?\n\n` +
      `${lineInfo}\n\n` +
      `Dette vil:\n` +
      `• Sette plukket antall til 0\n` +
      `• Flytte varene tilbake til original lokasjon\n` +
      `• Slette eventuelle pall-tilordninger\n` +
      `• Slette eventuelle serienummer-registreringer\n` +
      `• Slette eventuelle batch-nummer tilordninger\n\n` +
      `Velg "OK" for å angre plukkingen.`
    );

    if (!confirmed) {
      return; // User cancelled
    }

    try {
      // Get StartDateTime for this line from state (even though resetting to 0)
      const startDateTime = lineStartTimes.get(lineNo);

      // Update picked quantity to 0 in PListLine - this triggers backend reversal logic
      await pickingService.updatePickedQuantity(pListId, lineNo, 0, undefined, startDateTime);
      
      // Get and delete all SSCC records for this line
      const allSSCCs = await pickingService.getOrderSSCCs(pListId);
      const lineSSCCs = allSSCCs.filter(sscc => sscc.lineNo === lineNo);

      for (const sscc of lineSSCCs) {
        await pickingService.deleteSSCC(pListId, sscc.id);
      }

      // Delete all OrderSerialNo records for this line (used in ERP export)
      if (line) {
        await pickingService.deleteSerialNumbersByLine(pListId, line.orderNo, line.lineNo);
      }

      // Reload pallets from database
      await loadPallets();
      
      // Update local state
      const updatedLines = pickingLines.map(l =>
        l.pListLineNo === lineNo
          ? {
              ...l,
              qtyPicked: 0,
              isCompleted: false,
              isPartiallyPicked: false,
              isFullyPicked: false,
              palletId: undefined
            }
          : l
      );

      // Reorganize
      const reorganized = reorganizeLines(updatedLines);
      setPickingLines(reorganized);
      setActiveLineIndex(0);
      setCurrentLineIndex(0);

      console.log('Undid picking for line:', lineNo);
    } catch (error) {
      console.error('Error undoing pick:', error);
      alert('Kunne ikke angre plukk. Prøv igjen.');
    }
  };

  // Print SSCC label for the active pallet
  const handlePrintActivePalletSSCC = async () => {
    if (!activePallet || !user?.companyNo) return;

    setPrintingActivePalletSSCC(true);
    try {
      // Find the pallet summary to get item details
      const palletSummary = pallets.find(p => p.sscc === activePallet.sscc);

      const result = await printingService.printSSCCLabel(user.companyNo, {
        sscc: activePallet.sscc,
        quantity: palletSummary?.totalQuantity || undefined,
        labelType: SSCCLabelType.StandardPallet,
        gtin: palletSummary?.items?.[0]?.gtin || undefined,
        batchNo: palletSummary?.items?.[0]?.itemBatchNo || undefined,
        expiryDate: palletSummary?.items?.[0]?.itemBatchExpDate || undefined
      });

      if (!result.success) {
        alert(result.message || 'Feil ved utskrift av SSCC-etikett');
      }
    } catch (err: any) {
      console.error('Failed to print active pallet SSCC label:', err);
      alert(err.message || 'Feil ved utskrift av SSCC-etikett');
    } finally {
      setPrintingActivePalletSSCC(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 animate-spin border-2 border-blue-500 border-t-transparent rounded-full" />
        <span className="ml-3 text-gray-600">Laster plukk-detaljer...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">{error}</p>
        <button 
          onClick={() => loadPickingDetails()}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          Prøv igjen
        </button>
      </div>
    );
  }

  if (!pickingDetails) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Plukkliste ikke funnet</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile/Enterprise scanner support (Zebra, etc.) - invisible input that captures scanner data */}
      <MobileBarcodeCaptureInput
        enabled={barcodeScannerEnabled && !isAnyModalOpen}
        onScan={(barcode) => {
          console.log('📱 Mobile scanner detected barcode:', barcode);
          processGlobalBarcode(barcode);
        }}
      />

      {/* Header - Mobile optimized with sticky positioning */}
      <div className="bg-white border-b border-gray-200 p-2 sm:p-4 sticky top-0 z-20">
        <div className="flex items-center justify-between gap-2 max-w-6xl mx-auto">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-shrink-1">
            <BackButton onClick={onBack} />
            <h1 className="text-base sm:text-xl font-semibold truncate">Plukk #{pickingDetails.header.pListID}</h1>
          </div>

          {/* Action buttons - horizontally scrollable on narrow screens */}
          <div className="flex items-center gap-0.5 sm:gap-2 overflow-x-auto flex-shrink-0 -mr-2 sm:mr-0 pl-1">
            <button
              onClick={() => setShowPrintModal(true)}
              className="flex items-center justify-center min-w-[32px] w-8 h-8 sm:min-w-[44px] sm:w-11 sm:h-11 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              title="Skriv ut (gammel)"
            >
              <Printer size={18} className="sm:hidden" />
              <Printer size={20} className="hidden sm:block" />
            </button>
            <button
              onClick={() => setShowPickingPrintModal(true)}
              className="flex items-center justify-center min-w-[32px] w-8 h-8 sm:min-w-[44px] sm:w-11 sm:h-11 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              title="Alle etiketter"
            >
              <Tags size={18} className="sm:hidden" />
              <Tags size={20} className="hidden sm:block" />
            </button>
            {activePallet && (
              <button
                onClick={handlePrintActivePalletSSCC}
                disabled={printingActivePalletSSCC}
                className="flex items-center justify-center min-w-[32px] w-8 h-8 sm:min-w-[44px] sm:w-11 sm:h-11 text-green-600 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-50"
                title={`Skriv ut SSCC-etikett for aktiv pall (${activePallet.sscc})`}
              >
                {printingActivePalletSSCC ? (
                  <div className="w-4 h-4 sm:w-5 sm:h-5 animate-spin border-2 border-green-600 border-t-transparent rounded-full" />
                ) : (
                  <>
                    <ScanBarcode size={18} className="sm:hidden" />
                    <ScanBarcode size={20} className="hidden sm:block" />
                  </>
                )}
              </button>
            )}
            <button className="hidden sm:flex items-center justify-center min-w-[44px] w-11 h-11 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
              <BarChart3 size={20} />
            </button>
            <button className="hidden sm:flex items-center justify-center min-w-[44px] w-11 h-11 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
              <Info size={20} />
            </button>
            <button
              onClick={() => setShowPalletManager(!showPalletManager)}
              className="relative flex items-center justify-center min-w-[32px] w-8 h-8 sm:min-w-[44px] sm:w-11 sm:h-11 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <Package2 size={18} className="sm:hidden" />
              <Package2 size={20} className="hidden sm:block" />
              {activePallet && (
                <div className="absolute -top-0.5 -right-0.5 sm:-top-1 sm:-right-1 w-2.5 h-2.5 sm:w-3 sm:h-3 bg-green-500 rounded-full"></div>
              )}
            </button>
            <button
              onClick={() => setShowSignatureModal(true)}
              className="flex items-center justify-center min-w-[32px] w-8 h-8 sm:min-w-[44px] sm:w-11 sm:h-11 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              title="Legg til signatur"
            >
              <PenTool size={18} className="sm:hidden" />
              <PenTool size={20} className="hidden sm:block" />
            </button>
            <MediaCaptureButton
              entityType="PList"
              entityKey={pListId.toString()}
            />
            <button
              onClick={() => setShowAddLineModal(true)}
              className="flex items-center justify-center min-w-[32px] w-8 h-8 sm:min-w-[44px] sm:w-11 sm:h-11 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
              title="Legg til vare"
            >
              <Plus size={18} className="sm:hidden" />
              <Plus size={20} className="hidden sm:block" />
            </button>
            <button
              onClick={() => setShowSettingsModal(true)}
              className="flex items-center justify-center min-w-[32px] w-8 h-8 sm:min-w-[44px] sm:w-11 sm:h-11 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              title="Innstillinger"
            >
              <Settings size={18} className="sm:hidden" />
              <Settings size={20} className="hidden sm:block" />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-2 sm:p-4">
        {/* Mobile: Scanner at top, then content. Desktop: Side by side */}
        <div className="flex flex-col lg:grid lg:grid-cols-4 gap-4 sm:gap-6">
          {/* Scanner Column - Full width on mobile, side column on desktop */}
          <div className="order-first lg:order-none">
            <GS1Scanner
              ref={scannerRef}
              onScan={handleScan}
              pallets={palletSummaries}
              activePallet={activePalletSummary}
              onSetActivePallet={setActivePallet}
              onCreatePallet={createNewPallet}
              onCompletePicking={handleCompletePicking}
              onSendToPacking={handleSendToPacking}
            />
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Active Line */}
            <ActiveLine
              activeLine={activeLine ? convertToPickingLine(activeLine) : null}
              onScanRequired={() => {}}
              onLocationRequired={() => {}}
              editingLocation={editingLocation}
              editingQuantity={editingQuantity}
              newLocation={newLocation}
              newQuantity={newQuantity}
              onStartLocationEdit={(lineNo, location) => {
                setEditingLocation(lineNo);
                setNewLocation(location);
              }}
              onStartQuantityEdit={(lineNo, quantity) => {
                setEditingQuantity(lineNo);
                setNewQuantity(quantity);
              }}
              onLocationChange={setNewLocation}
              onQuantityChange={setNewQuantity}
              onLocationEdit={handleLocationEdit}
              onQuantityEdit={handleQuantityEdit}
              onShowImage={handleShowImage}
            />

            {/* ItemType=6: Serial-tracked inventory - info banner */}
            {activeLine && activeLine.itemType === 6 && (
              <div className="mb-2 sm:mb-4 p-2 sm:p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div className="text-sm text-emerald-800 font-medium">
                    Serienummerstyrt vare - velg serienummer ved plukk
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-emerald-700">
                      Plukket: {Math.floor(activeLine.qtyPicked)} / {Math.floor(activeLine.qty)}
                    </span>
                    {activeLine.qtyPicked < activeLine.qty && (
                      <button
                        onClick={() => {
                          setSerialPickLine(activeLine);
                          setShowSerialPickModal(true);
                        }}
                        className="flex items-center gap-2 px-3 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-sm font-medium"
                      >
                        Velg serienummer
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* ItemType=4/5: Batch-tracked inventory - info banner */}
            {activeLine && (activeLine.itemType === 4 || activeLine.itemType === 5) && (
              <div className="mb-2 sm:mb-4 p-2 sm:p-3 bg-orange-50 border border-orange-200 rounded-lg">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div className="text-sm text-orange-800 font-medium">
                    Batchstyrt vare - velg batch ved plukk
                    {activeLine.requestedBatchNo && (
                      <span className="ml-2 text-orange-600 font-normal">
                        (Forventet: <span className="font-mono">{activeLine.requestedBatchNo}</span>)
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-orange-700">
                      Plukket: {activeLine.qtyPicked} / {activeLine.qty}
                    </span>
                    {activeLine.qtyPicked < activeLine.qty && (
                      <button
                        onClick={() => {
                          setBatchPickLine(activeLine);
                          setShowBatchPickModal(true);
                        }}
                        className="flex items-center gap-2 px-3 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-sm font-medium"
                      >
                        Velg batch
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Serial/Batch Number Registration Buttons - show when active line requires it */}
            {activeLine && ((activeLine.serialNoItemYesNo && activeLine.itemType !== 6) || activeLine.batchItemYesNo) && (
              <div className="mb-2 sm:mb-4 p-2 sm:p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div className="text-sm text-amber-800 font-medium">
                    Denne varen krever registrering av{' '}
                    {activeLine.serialNoItemYesNo && activeLine.batchItemYesNo
                      ? 'serienummer og batchnummer'
                      : activeLine.serialNoItemYesNo
                      ? 'serienummer'
                      : 'batchnummer'}
                  </div>
                  <div className="flex gap-2">
                    {activeLine.serialNoItemYesNo && (
                      <button
                        onClick={() => {
                          setSerialNumberLine(activeLine);
                          setShowSerialNumberModal(true);
                        }}
                        className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                      >
                        <span className="hidden sm:inline">Serienummer</span>
                        <span className="sm:hidden">Serie</span>
                        {activeLine.serialNumbers && activeLine.serialNumbers.length > 0 && (
                          <span className="bg-blue-500 px-1.5 py-0.5 rounded text-xs">
                            {activeLine.serialNumbers.length}/{Math.floor(activeLine.qty)}
                          </span>
                        )}
                      </button>
                    )}
                    {activeLine.batchItemYesNo && (
                      <button
                        onClick={() => {
                          setBatchNumberLine(activeLine);
                          setShowBatchNumberModal(true);
                        }}
                        className="flex items-center gap-2 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm font-medium"
                      >
                        <span className="hidden sm:inline">Batchnummer</span>
                        <span className="sm:hidden">Batch</span>
                        {activeLine.batchNumbers && activeLine.batchNumbers.length > 0 && (
                          <span className="bg-purple-500 px-1.5 py-0.5 rounded text-xs">
                            {activeLine.batchNumbers.length}
                          </span>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            <PickingLinesList
              lines={pickingLines.map(convertToPickingLine)}
              currentLineIndex={currentLineIndex}
              activeLineIndex={activeLineIndex}
              pallets={palletSummaries}
              editingLocation={editingLocation}
              editingQuantity={editingQuantity}
              newLocation={newLocation}
              newQuantity={newQuantity}
              showPalletInfo={showPalletInfo}
              onLineClick={handleLineSelect}
              onLocationEdit={handleLocationEdit}
              onQuantityEdit={handleQuantityEdit}
              onStartLocationEdit={(lineNo, location) => {
                setEditingLocation(lineNo);
                setNewLocation(location);
              }}
              onStartQuantityEdit={(lineNo, quantity) => {
                setEditingQuantity(lineNo);
                setNewQuantity(quantity);
              }}
              onLocationChange={setNewLocation}
              onQuantityChange={setNewQuantity}
              onSplitLine={handleSplitLine}
              onPickLine={handlePickLine}
              onUndoPicking={handleUndoPicking}
              onShowPalletInfo={setShowPalletInfo}
              onReorder={handleReorder}
              enableDragAndDrop={enableDragAndDrop}
            />
          </div>
        </div>
      </div>

      {/* Image Panel */}
      <ImagePanel
        isOpen={showImagePanel && !!activeLine?.image}
        imageUrl={activeLine?.image || ''}
        itemDescription={activeLine?.itemDescription || ''}
        onZoom={() => {
          if (activeLine?.image) {
            setSelectedImage(activeLine.image);
            setShowImageModal(true);
          }
        }}
        onClose={() => setShowImagePanel(false)}
      />

      {/* Modals */}
      <LocationScanModal
        isOpen={showLocationScan}
        expectedLocation={currentLine?.location || ''}
        tempGS1Quantity={tempGS1Quantity}
        locationScan={locationScan}
        onLocationScanChange={setLocationScan}
        onConfirm={handleLocationScanConfirm}
        onOverride={() => setShowLocationOverride(true)}
        onClose={() => {
          setShowLocationScan(false);
          setLocationScan('');
          setTempGS1Quantity(null);
        }}
      />

      <LocationOverrideModal
        isOpen={showLocationOverride}
        expectedLocation={currentLine?.location || ''}
        overrideLocation={overrideLocation}
        companyNo={pickingDetails.header.companyNo}
        warehouseID={currentLine?.warehouseID || ''}
        itemNo={currentLine?.itemNo || ''}
        requiredQuantity={currentLine ? currentLine.qty - currentLine.qtyPicked : 0}
        onLocationChange={setOverrideLocation}
        onConfirm={handleLocationOverride}
        onClose={() => {
          setShowLocationOverride(false);
          setOverrideLocation('');
        }}
      />

      <ImageModal
        isOpen={showImageModal}
        imageUrl={selectedImage}
        onClose={() => setShowImageModal(false)}
      />

      <SplitModal
        isOpen={showSplitModal}
        line={convertToPickingLineWithCheck(splitLineNo ? pickingLines.find(l => l.pListLineNo === splitLineNo) : undefined)}
        pallets={palletSummaries}
        splitQuantities={splitQuantities}
        onQuantityChange={(palletId, quantity) => setSplitQuantities({
          ...splitQuantities,
          [palletId]: quantity
        })}
        onConfirm={confirmSplit}
        onClose={closeSplitModal}
      />

      <QuantityDistributionModal
        isOpen={showQuantityDistribution}
        line={convertToPickingLineWithCheck(distributionLineNo ? pickingLines.find(l => l.pListLineNo === distributionLineNo) : undefined)}
        pallets={palletSummaries}
        distributionQuantities={distributionQuantities}
        onQuantityChange={(palletId, quantity) => setDistributionQuantities({
          ...distributionQuantities,
          [palletId]: quantity
        })}
        onConfirm={confirmQuantityDistribution}
        onClose={closeDistributionModal}
      />

      <PalletInfoModal
        isOpen={!!showPalletInfo}
        pallet={showPalletInfo ? palletSummaries.find(p => p.sscc === showPalletInfo) || null : null}
        onClose={() => setShowPalletInfo(null)}
      />

      <DeliveryInfoModal
        isOpen={showDeliveryInfoModal}
        pListId={pListId}
        companyNo={pickingDetails?.header.companyNo || ''}
        pListOrders={pListOrders}
        userProfile={userProfile}
        onConfirm={handleDeliveryInfoConfirm}
        onClose={() => setShowDeliveryInfoModal(false)}
      />

      {/* Pack zone selection modal for "Til Pakking" */}
      <PackZoneSelectionModal
        isOpen={showPackZoneSelection}
        pListId={pListId}
        pListOrders={pListOrders}
        onConfirm={handlePackZoneConfirm}
        onClose={() => setShowPackZoneSelection(false)}
      />

      {/* Package scan modal for batch picking - must scan correct package before quantity entry */}
      <PackageScanModal
        isOpen={showPackageScan}
        onClose={() => {
          setShowPackageScan(false);
          setLineToConfirm(null);
          setQuantityToConfirm(0);
        }}
        onConfirm={handlePackageScanConfirm}
        packageNo={lineToConfirm?.packageNo ?? 0}
        visiblePackageNo={lineToConfirm?.visiblePackageNo}
        itemNo={lineToConfirm?.itemNo}
        customerName={lineToConfirm?.customerName}
      />

      <QuantityConfirmModal
        isOpen={showQuantityConfirm}
        line={lineToConfirm}
        proposedQuantity={quantityToConfirm}
        onConfirm={handleQuantityConfirm}
        onClose={() => {
          setShowQuantityConfirm(false);
          setLineToConfirm(null);
          setQuantityToConfirm(0);
        }}
      />

      <QuantityDeviationModal
        isOpen={showQuantityDeviation}
        deviations={quantityDeviations}
        onConfirm={handleQuantityDeviationConfirm}
        onCancel={handleQuantityDeviationCancel}
      />

      <SignatureModal
        isOpen={showSignatureModal}
        onConfirm={handleSignatureConfirm}
        onClose={() => setShowSignatureModal(false)}
      />

      <MandatoryCountingModal
        isOpen={showMandatoryCounting}
        itemNo={mandatoryCountingData?.itemNo || ''}
        itemDescription={mandatoryCountingData?.itemDescription || ''}
        binCode={mandatoryCountingData?.binCode || ''}
        lastCountedDate={mandatoryCountingData?.lastCountedDate}
        daysSinceLastCounted={mandatoryCountingData?.daysSinceLastCounted}
        currentQuantity={mandatoryCountingData?.currentQuantity || 0}
        onSubmit={handleMandatoryCountingSubmit}
        isSubmitting={isSubmittingCounting}
      />

      <ZeroPointCountingModal
        isOpen={showZeroPointCounting}
        itemNo={zeroPointCountingData?.itemNo || ''}
        itemDescription={zeroPointCountingData?.itemDescription || ''}
        binCode={zeroPointCountingData?.binCode || ''}
        pickedQuantity={zeroPointCountingData?.pickedQuantity || 0}
        expectedRemaining={zeroPointCountingData?.expectedRemaining || 0}
        onConfirmZero={handleZeroPointConfirmZero}
        onSubmitDeviation={handleZeroPointSubmitDeviation}
        isSubmitting={isSubmittingZeroPoint}
      />

      <SettingsModal
        isOpen={showSettingsModal}
        autoShowImage={autoShowImage}
        onAutoShowImageChange={setAutoShowImage}
        enableDragAndDrop={enableDragAndDrop}
        onEnableDragAndDropChange={setEnableDragAndDrop}
        enableRapidScanning={enableRapidScanning}
        onEnableRapidScanningChange={setEnableRapidScanning}
        onClose={() => setShowSettingsModal(false)}
      />

      <PrintModal
        isOpen={showPrintModal}
        onClose={() => setShowPrintModal(false)}
        companyNo={pickingDetails?.header.companyNo || ''}
        documentTitle={pickingDetails ? `Plukkliste #${pickingDetails.header.pListID}` : 'Plukkliste'}
        pListId={pListId}
        pickingDetails={pickingDetails}
      />

      {/* New Picking Print Modal with all label types */}
      <PickingPrintModal
        isOpen={showPickingPrintModal}
        onClose={() => setShowPickingPrintModal(false)}
        pickingDetails={pickingDetails}
        activeLine={activeLine}
      />

      {/* Serial Number Registration Modal (ItemType=2 - free-text entry) */}
      <SerialNumberModal
        isOpen={showSerialNumberModal}
        line={serialNumberLine}
        pListId={pListId}
        onClose={() => {
          setShowSerialNumberModal(false);
          setSerialNumberLine(null);
        }}
        onSerialNumbersChanged={() => {
          // Refresh picking details to update serial numbers
          loadPickingDetails();
        }}
      />

      {/* Serial Pick Modal (ItemType=6 - select from inventory) */}
      <SerialPickModal
        isOpen={showSerialPickModal}
        line={serialPickLine}
        pListId={pListId}
        onClose={() => {
          setShowSerialPickModal(false);
          setSerialPickLine(null);
        }}
        onSerialPicked={handleSerialPicked}
      />

      {/* Batch Pick Modal (ItemType=4/5 - select batch from inventory) */}
      <BatchPickModal
        isOpen={showBatchPickModal}
        line={batchPickLine}
        pListId={pListId}
        onClose={() => {
          setShowBatchPickModal(false);
          setBatchPickLine(null);
        }}
        onBatchPicked={handleBatchPicked}
      />

      {/* Batch Number Registration Modal */}
      <BatchNumberModal
        isOpen={showBatchNumberModal}
        line={batchNumberLine}
        pListId={pListId}
        onClose={() => {
          setShowBatchNumberModal(false);
          setBatchNumberLine(null);
        }}
        onBatchNumbersChanged={() => {
          // Refresh picking details to update batch numbers
          loadPickingDetails();
        }}
      />

      {/* Pallet Manager */}
      {showPalletManager && (
        <PalletManager
          pallets={palletSummaries}
          activePalletSSCC={activePalletSSCC}
          onCreatePallet={createNewPallet}
          onSetActivePallet={setActivePallet}
          onDeletePallet={deletePallet}
          onClose={() => setShowPalletManager(false)}
        />
      )}

      {/* Add Line Modal */}
      <AddLineModal
        isOpen={showAddLineModal}
        pListId={pListId}
        onClose={() => setShowAddLineModal(false)}
        onSuccess={() => {
          loadPickingDetails(); // Refresh picking list to show new line
        }}
      />

      {/* Bottom Status Bar */}
      <StatusBar
        completedLines={completedLines}
        totalLines={pickingLines.length}
        pallets={palletSummaries}
        activePalletSSCC={activePalletSSCC}
        priority={pickingDetails.header.priority}
      />
    </div>
  );
};